// src/components/Auth.tsx
import React, { useState } from "react";
// FIX: Changed import path for auth to solve module resolution issues.
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, firebaseError } from "@/firebase"; // ← 重要: パスは "@/firebase"
import { initializeUserData } from "../services/firestoreService";
import ErrorDisplay from "./ErrorDisplay";

type ErrorState = { message: string; details: string } | null;

interface AuthProps {
  onLoginSuccess?: () => void;
}

const Auth: React.FC<AuthProps> = ({ onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ErrorState>(null);
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  // Firebase 初期化エラーを UI 用に整形（ErrorDisplay が {message,details} を想定している前提）
  const firebaseBanner: ErrorState = firebaseError
    ? {
        message: "Firebase設定が不足しているか無効です。アプリの保存・読込機能は利用できません。",
        details: firebaseError,
      }
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // auth が初期化失敗(null)のときは早期リターン
    if (!auth) {
      const details =
        firebaseError ??
        "Firebase Auth object could not be initialized. Check Firebase config.";
      setError({
        message: "認証サービスが利用できません。",
        details,
      });
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        // 新規ユーザーの Firestore 初期データ
        await initializeUserData(userCredential.user.uid);
      }
      // ログイン成功時のコールバックを呼び出し
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (err: any) {
      const code: string = err?.code ?? "unknown";
      const message: string = getFriendlyErrorMessage(code);
      setError({
        message,
        details: `Error Code: ${code}\nMessage: ${err?.message ?? String(err)}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);

    if (!auth) {
      const details =
        firebaseError ??
        "Firebase Auth object could not be initialized. Check Firebase config.";
      setError({
        message: "認証サービスが利用できません。",
        details,
      });
      setLoading(false);
      return;
    }

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // 新規ユーザーの場合は初期データを作成
      if (result._tokenResponse?.isNewUser) {
        await initializeUserData(result.user.uid);
      }
      
      // ログイン成功時のコールバックを呼び出し
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (err: any) {
      const code: string = err?.code ?? "unknown";
      const message: string = getFriendlyErrorMessage(code);
      setError({
        message,
        details: `Error Code: ${code}\nMessage: ${err?.message ?? String(err)}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth) {
      setError({
        message: "認証サービスが利用できません。",
        details: "Firebase Auth object could not be initialized.",
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await sendPasswordResetEmail(auth, email);
      setResetMessage("パスワードリセットメールを送信しました。メールボックスをご確認ください。");
      setShowPasswordReset(false);
    } catch (err: any) {
      const code: string = err?.code ?? "unknown";
      const message: string = getFriendlyErrorMessage(code);
      setError({
        message,
        details: `Error Code: ${code}\nMessage: ${err?.message ?? String(err)}`,
      });
    } finally {
      setLoading(false);
    }
  };

  function getFriendlyErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case "auth/configuration-not-found":
        // サインイン方式が未構成／Authorized domains 未登録など
        return "このサインイン方式の設定が見つかりません。Firebase Consoleの Authentication で「Email/Password」を有効化し、Authorized domains に現在のURL（localhost等）を追加してください。";
      case "auth/operation-not-allowed":
        // プロバイダは存在するが無効
        return "このサインイン方式は現在無効です。Firebase Consoleで「Email/Password」を有効化してください。";
      case "auth/email-already-in-use":
        return "このメールアドレスはすでに登録されています。";
      case "auth/invalid-email":
        return "メールアドレスの形式が正しくありません。";
      case "auth/weak-password":
        return "パスワードが弱すぎます（6文字以上にしてください）。";
      case "auth/user-not-found":
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "メールアドレスまたはパスワードが間違っています。";
      case "auth/network-request-failed":
        return "ネットワークエラーが発生しました。接続状況を確認して再度お試しください。";
      case "auth/too-many-requests":
        return "リクエストが多すぎます。しばらく待ってからお試しください。";
      default:
        return "認証中にエラーが発生しました。もう一度お試しください。";
    }
  }

  return (
    <div className="w-full max-w-md bg-white/60 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-200/50 mx-auto text-left">
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
        {isLogin ? "ログイン" : "新規登録"}
      </h2>

      {/* Firebase 初期化エラー（折りたたみ式の ErrorDisplay を想定） */}
      <ErrorDisplay error={firebaseBanner} />

      {/* 処理時の個別エラー */}
      <ErrorDisplay error={error} />

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            メールアドレス
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={!!firebaseBanner}
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition disabled:bg-gray-200"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-gray-700"
          >
            パスワード
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={isLogin ? "current-password" : "new-password"}
            disabled={!!firebaseBanner}
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition disabled:bg-gray-200"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={loading || !!firebaseBanner}
            className="w-full bg-red-800 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-red-900 transition-colors duration-300 disabled:bg-red-300 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading && (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            )}
            {isLogin ? "ログイン" : "登録する"}
          </button>
        </div>

        {/* パスワードリセットリンク（ログイン時のみ表示） */}
        {isLogin && (
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => setShowPasswordReset(true)}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              パスワードを忘れた場合
            </button>
          </div>
        )}
      </form>

      {/* パスワードリセットフォーム */}
      {showPasswordReset && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">パスワードリセット</h3>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                メールアドレス
              </label>
              <input
                type="email"
                id="reset-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={!!firebaseBanner}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 transition disabled:bg-gray-200"
                placeholder="登録時のメールアドレスを入力"
              />
            </div>
            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={loading || !!firebaseBanner}
                className="flex-1 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300 disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {loading ? "送信中..." : "リセットメールを送信"}
              </button>
              <button
                type="button"
                onClick={() => setShowPasswordReset(false)}
                className="flex-1 bg-gray-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors duration-300"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* パスワードリセット成功メッセージ */}
      {resetMessage && (
        <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {resetMessage}
        </div>
      )}

      {/* Googleログインボタン */}
      <div className="mt-4">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white/60 text-gray-500">または</span>
          </div>
        </div>
        
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={loading || !!firebaseBanner}
          className="mt-4 w-full bg-white text-gray-700 font-medium py-3 px-8 rounded-lg shadow-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-300 disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Googleでログイン
        </button>
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={() => {
            setIsLogin(!isLogin);
            setError(null);
          }}
          className="text-sm text-red-700 hover:underline disabled:text-gray-500 disabled:no-underline"
          disabled={!!firebaseBanner}
        >
          {isLogin
            ? "アカウントをお持ちでないですか？ 新規登録"
            : "すでにアカウントをお持ちですか？ ログイン"}
        </button>
      </div>
    </div>
  );
};

export default Auth;
